import { lazy, Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, FolderKanban, Plus, ShoppingCart, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ROUTES } from '@/lib/routes';
import { usePermissions } from '@/api/permissions';
import { can, canOpen } from '@/lib/permissions';

// The CSP request dialog fetches CSP data as soon as it renders, so it's loaded (and mounted) only on demand.
const RequestDialog = lazy(() => import('@/components/csp/CspKit').then((m) => ({ default: m.RequestDialog })));

/** Header "New" menu: quick create for the records people make most, from any page. */
export function NewMenu() {
  const navigate = useNavigate();
  const [requestOpen, setRequestOpen] = useState(false);
  const perms = usePermissions();
  // RBAC: only what the role can create.
  const show = {
    ticket: canOpen(perms, ROUTES.psa.tickets.new), opportunity: canOpen(perms, ROUTES.sales.opportunities.new),
    project: canOpen(perms, ROUTES.psa.projects.new), csp: can(perms, 'csp.requests', 'full'),
  };
  if (!Object.values(show).some(Boolean)) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1 px-2.5" aria-label="New">
            <Plus className="h-4 w-4"/>
            <span className="hidden sm:inline">New</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {show.ticket && (
            <DropdownMenuItem onSelect={() => navigate(ROUTES.psa.tickets.new)}>
              <Ticket className="mr-2 h-4 w-4"/> New ticket
            </DropdownMenuItem>
          )}
          {show.opportunity && (
            <DropdownMenuItem onSelect={() => navigate(ROUTES.sales.opportunities.new)}>
              <Briefcase className="mr-2 h-4 w-4"/> New opportunity
            </DropdownMenuItem>
          )}
          {show.project && (
            <DropdownMenuItem onSelect={() => navigate(ROUTES.psa.projects.new)}>
              <FolderKanban className="mr-2 h-4 w-4"/> New project
            </DropdownMenuItem>
          )}
          {show.csp && (
            <DropdownMenuItem onSelect={() => setRequestOpen(true)}>
              <ShoppingCart className="mr-2 h-4 w-4"/> New CSP request
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {requestOpen && (
        <Suspense fallback={null}>
          <RequestDialog open onOpenChange={(o) => { if (!o) setRequestOpen(false); }} />
        </Suspense>
      )}
    </>
  );
}
